'use client';

import { FC, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tabs, { tabsClasses } from '@mui/material/Tabs';

import LinkGenerator from './link-generator';
import { useTranslate } from '../../../../../locales';
import Iconify from '../../../../../components/iconify';
import ProfileHistoryTable from './profile-history-table';
import ProfileMyAgentTable from './profile-my-agent-table';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { useSettingsContext } from '../../../../../components/settings';
import { usePopover } from '../../../../../components/custom-popover';
import ProfileAffiliateRulesTable from './profile-affiliate-rules-table';
import { ComponentTypeProps } from '../../../../../types/page-generator';

const CryptoAffiliate: FC<ComponentTypeProps> = ({ block }) => {
  const defaultButton = () => {
    if (block?.settings?.enableMyAgents) return 'myAgents';
    if (!block?.settings?.enableMyAgents && block?.settings?.enableRewardHistory)
      return 'rewardHistory';
    if (
      !block?.settings?.enableMyAgents &&
      !block?.settings?.enableRewardHistory &&
      block?.settings?.enableConditions
    )
      return 'conditions';
    if (
      !block?.settings?.enableMyAgents &&
      !block?.settings?.enableRewardHistory &&
      !block?.settings?.enableConditions &&
      block?.settings?.enableReference
    )
      return 'reference';
    if (
      !block?.settings?.enableMyAgents &&
      !block?.settings?.enableRewardHistory &&
      !block?.settings?.enableConditions &&
      !block?.settings?.enableReference
    )
      return '';
    return ''; // fallback
  };

  const isMobile = useResponsive('down', 'sm');
  const { t } = useTranslate();
  const settings = useSettingsContext();
  const popover = usePopover();
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(defaultButton);
  const [sort, setSort] = useState('2023');
  const [activeButton, setActiveButton] = useState<'customer' | 'agent' | null>('customer');

  const TABS = [
    ...(block?.settings?.enableMyAgents
      ? [
          {
            value: 'myAgents',
            label: 'My Agents',
            icon: (
              <Iconify
                icon="mynaui:circle-half-circle-solid"
                width={24}
                sx={{ transform: 'rotate(90deg)' }}
              />
            ),
          },
        ]
      : []),

    ...(block?.settings?.enableRewardHistory
      ? [
          {
            value: 'rewardHistory',
            label: 'Reward History',
            icon: <Iconify icon="solar:hand-stars-broken" width={24} />,
          },
        ]
      : []),

    ...(block?.settings?.enableConditions
      ? [
          {
            value: 'conditions',
            label: 'Rules',
            icon: <Iconify icon="hugeicons:task-done-02" width={24} />,
          },
        ]
      : []),

    ...(block?.settings?.enableReference
      ? [
          {
            value: 'reference',
            label: 'Reference',
            icon: <Iconify icon="mdi:users" width={24} />,
          },
        ]
      : []),
  ];

  const onSort = useCallback((newValue: string) => {
    setSort(newValue);
  }, []);

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);

    // const element = document.getElementById(newValue);
    // if (element) {
    //   element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // }
  }, []);

  return (
    <Container
      maxWidth={settings.themeStretch ? false : 'xl'}
      sx={{ mt: 2, padding: '0 !important' }}
    >
      <Stack>
        <Card sx={{ py: '30px' }}>
          <Tabs
            value={currentTab}
            onChange={handleChangeTab}
            sx={{
              width: 1,
              bottom: 0,
              zIndex: 9,
              position: 'absolute',
              bgcolor: 'background.paper',
              [`& .${tabsClasses.flexContainer}`]: {
                pr: { md: 3 },
                height: 50,
                justifyContent: {
                  sm: 'center',
                  md: 'flex-end',
                },
                alignItems: 'center',
              },
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} icon={tab.icon} label={t(tab.label)} />
            ))}
          </Tabs>
        </Card>
        {currentTab === 'rewardHistory' && (
          <Box>
            <Typography id="rewardHistory" variant="h6" sx={{ my: 3, scrollMarginTop: '80px' }}>
              History
            </Typography>
            <ProfileHistoryTable />
          </Box>
        )}
        {currentTab === 'myAgents' && (
          <Box>
            <Typography id="myAgents" variant="h6" sx={{ my: 3, scrollMarginTop: '80px' }}>
              My Agents
            </Typography>
            <ProfileMyAgentTable />
          </Box>
        )}
        {currentTab === 'reference' && (
          <Box>
            <Typography variant="h6" sx={{ my: 3 }}>
              Reference
            </Typography>
            <Typography id="conditions" sx={{ my: 2, scrollMarginTop: '80px' }}>
              Generate Link
            </Typography>
            <Stack direction="row" width={1}>
              <Stack
                onClick={() => setActiveButton('customer')}
                sx={{
                  border: activeButton === 'customer' ? '2px solid black' : 'none',
                  borderRadius: 1,
                  width: 1,
                  p: isMobile ? 2 : 5,
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                direction="row"
                spacing={1}
              >
                <Iconify icon="heroicons:users-20-solid" />
                <Typography>Customer Link</Typography>
              </Stack>

              <Stack
                onClick={() => setActiveButton('agent')}
                sx={{
                  border: activeButton === 'agent' ? '2px solid black' : 'none',
                  borderRadius: 1,
                  width: 1,
                  p: isMobile ? 2 : 5,
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                direction="row"
                spacing={1}
              >
                <Iconify icon="ion:resize" />
                <Typography>Agent Link</Typography>
              </Stack>
            </Stack>
            <LinkGenerator />
          </Box>
        )}
        {currentTab === 'conditions' && (
          <Box>
            <Typography variant="h6" sx={{ my: 3 }}>
              Affiliate Rules
            </Typography>
            <ProfileAffiliateRulesTable />
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default CryptoAffiliate;
